'use client';

import { useAuth } from '@/hooks/useAuth';
import { MyBatches } from '@/hooks/useMyBatches';
import { Client } from '@/lib/api';
import { ApiServiceTypeEnum } from '@/lib/api/api-client';
import { Cohort, CohortApiService } from '@/lib/api/cohort-api';
import { JourneyHomeResponse } from '@/lib/api/journey-home-api';
import { triggerMoengageLoginIfUserIsPresent } from '@/lib/events';
import { ClientConfigType, MetadataBatchConfig, MetadataEbcConfig } from '@/types/client-config';
import { hydrateSessionFromAppQuery } from '@/utils/app-iframe-session';
import { getSessionStorageValue, setSessionStorageValue } from '@/utils/common';
import { CookieKeys, CookieService } from '@/utils/cookie-service';
import { loadMoengageScript } from '@/utils/moengage';
import {
  clearPaidData,
  getPaidData,
  PaidData,
  setLocalPaidData,
  setPaidData,
} from '@/utils/paid-data';
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

interface User {
  status?: string;
  id?: string;
  firstName?: string;
  lastName?: string;
  primaryNumber?: string;
  countryCode?: string;
  countryGroup?: string;
  username?: string;
  uniqueCode?: string;
  isVerifiedEmail?: boolean;
  created?: string;
  createdAt?: string;
  profileId?: {
    _id?: string;
    class?: string;
    cohortId?: string;
    exams?: string[];
    board?: string;
    language?: string;
    programId?: string;
    wallet?: number;
    totalRewards?: number;
    coins?: {
      bankTransferCoins?: number;
      voucherCoins?: number;
      commonCoins?: number;
      walletCoins?: number;
      totalCoins?: number;
      redeemedCoins?: number;
    };
    parentDetails?: any;
    isProfileCompleted?: boolean;
    children?: any[];
    needCohortUpdate?: boolean;
    cohortUpdatedAt?: string;
  };
  dateOfBirth?: string | null;
  phones?: any[];
  address?: any;
  primary?: any;
  organization?: {
    _id?: string;
    status?: string;
    usersCount?: number;
    enrolmentType?: string;
    name?: string;
    website?: string;
    phones?: any[];
    primaryContact?: string;
    rejectionComments?: any[];
    createdAt?: string;
    updatedAt?: string;
  };
  roles?: Array<{
    id?: string;
    name?: string;
  }>;
  androidVersion?: number;
  iosVersion?: number;
  webVersion?: number;
  isScholar?: boolean;
  oneRoles?: any[];
  [key: string]: any;
}

interface UserContextType {
  user: User | null;
  isLoading: boolean;
  purchasedBatchesData: any;
  paidData: PaidData;
  journeyHomeData: JourneyHomeResponse | null;
  updateUser: (userData: User | null) => void;
  clearUser: () => void;
  handlePurchasedBatchesDataUpdate: (purchasedBatchesData: any) => void;
  updatePaidData: (paidData: PaidData) => void;
  clearPaidData: () => void;
  updateJourneyHomeData: (journeyData: JourneyHomeResponse | null) => void;
  isUserLoggedIn: boolean;
  refetchUserBatches: () => void;
  userSelectedCohortId?: string;
  handleUserSelectedCohortIdUpdate: (userSelectedCohortId: string) => void;
  handleCohortExploration: (cohortId: string) => void;
  handleActualCohortSwitch: (cohortId: string) => void;
  fetchUserData: () => Promise<User | null>;
  profileUpdateTrigger: number;
  triggerProfileUpdate: () => void;
  clientConfig?: ClientConfigType | null;
  fetchClientConfig?: (category?: string) => Promise<any>;
  globalRef?: React.RefObject<{ [key: string]: any }>;
  cohorts?: Cohort[];
}

const UserContext = createContext<UserContextType>({
  user: null,
  isLoading: true,
  purchasedBatchesData: null,
  paidData: {},
  journeyHomeData: null,
  updateUser: () => {},
  clearUser: () => {},
  handlePurchasedBatchesDataUpdate: () => {},
  updatePaidData: () => {},
  clearPaidData: () => {},
  updateJourneyHomeData: () => {},
  isUserLoggedIn: false,
  refetchUserBatches: async () => {},
  userSelectedCohortId: '',
  handleUserSelectedCohortIdUpdate: (cohortId: string) => {},
  handleCohortExploration: (cohortId: string) => {},
  handleActualCohortSwitch: (cohortId: string) => {},
  fetchUserData: () => Promise.resolve(null),
  profileUpdateTrigger: 0,
  triggerProfileUpdate: () => {},
  clientConfig: null,
  fetchClientConfig: (category?: string) => Promise.resolve(null),
  globalRef: { current: {} },
  cohorts: [],
});

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [purchasedBatchesData, setPurchasedBatchesData] = useState<any>(null);
  const [paidData, setPaidDataState] = useState<PaidData>({});
  const [userSelectedCohortId, setUserSelectedCohortId] = useState<string>('');
  const [journeyHomeData, setJourneyHomeData] = useState<JourneyHomeResponse | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [hasCalledAPI, setHasCalledAPI] = useState(false);
  const [profileUpdateTrigger, setProfileUpdateTrigger] = useState(0);
  const apiCallInProgress = useRef(false);
  const initializationInProgress = useRef(false);
  const [clientConfig, setClientConfig] = useState<ClientConfigType | null>({});
  const [cohorts, setCohorts] = useState<Cohort[]>([]);
  // a global variable to store values which does not need re-rendering but needs to be accessed across components
  const globalRef = useRef({});

  const { getMe } = useAuth();

  // Handle purchased batches data update with exact parity to Redux logic
  const handlePurchasedBatchesDataUpdate = useCallback(
    (purchasedBatchesData: any) => {
      if (purchasedBatchesData === null) {
        const paidData: PaidData = {
          user_status: 'non_loggedin',
          paid_batch_type: 'booster',
        };

        if (typeof window !== 'undefined') {
          window.localStorage.setItem('paidData', JSON.stringify(paidData));
        }
        setPaidDataState(paidData);
        setPurchasedBatchesData(null);
      } else {
        setLocalPaidData(purchasedBatchesData as MyBatches[] | null);
        setPurchasedBatchesData(purchasedBatchesData);
      }
    },
    [user]
  );

  useEffect(() => {
    if (!user) return;
    const fetchAllCohorts = async () => {
      const cohortResponse = await CohortApiService.getAllCohorts();
      return cohortResponse?.data || [];
    };
    fetchAllCohorts()
      .then(cohortsData => setCohorts(cohortsData))
      .catch((error: Error) => {
        console.error('Error fetching cohorts:', error);
      });
  }, [user]);

  useEffect(() => {
    if (user?.profileId?.cohortId) {
      // Only update if there's no session storage value (first time load)
      const sessionCohortId = getSessionStorageValue('selectedCohortId');
      if (!sessionCohortId) {
        handleUserSelectedCohortIdUpdate(user.profileId.cohortId);
      }
    }
  }, [user]);

  const handleUserSelectedCohortIdUpdate = useCallback((userSelectedCohortId: string) => {
    setUserSelectedCohortId(userSelectedCohortId);
    setSessionStorageValue('selectedCohortId', userSelectedCohortId);
  }, []);

  // Separate function for exploration (updates session storage)
  const handleCohortExploration = useCallback((cohortId: string) => {
    setUserSelectedCohortId(cohortId);
    setSessionStorageValue('selectedCohortId', cohortId);
  }, []);

  // Separate function for actual cohort switch (clears session storage)
  const handleActualCohortSwitch = useCallback((cohortId: string) => {
    setUserSelectedCohortId(cohortId);
    // Clear session storage since this is now the user's actual cohort
    if (typeof window !== 'undefined') {
      try {
        sessionStorage.removeItem('selectedCohortId');
      } catch (error) {
        console.warn('Failed to clear selectedCohortId from sessionStorage:', error);
      }
    }
  }, []);

  // Fetch user data from API - calls getMe from useAuth
  const fetchUserData = useCallback(async () => {
    if (apiCallInProgress.current) {
      console.log('API call in progress, skipping...');
      return null;
    }

    // Check if user has a valid token before making API call
    const token =
      CookieService.getValue(CookieKeys.TOKEN_CONTEXT)?.token ||
      localStorage.getItem('TOKEN') ||
      localStorage.getItem('token');

    if (!token) {
      console.log('No token found, skipping fetchUserData API call');
      return null;
    }

    apiCallInProgress.current = true;

    try {
      console.log('Calling getMe API...');
      const userData = await getMe();

      if (userData) {
        // Update user in context
        setUser(userData);

        // Initialize Moengage for user if exists
        if (userData.id) {
          loadMoengageScript();
          triggerMoengageLoginIfUserIsPresent();
        }

        console.log('API called successfully, user data updated');
      }

      return userData;
    } catch (error) {
      console.error('Error fetching user data from API:', error);
      return null;
    } finally {
      apiCallInProgress.current = false;
    }
  }, [getMe]);

  // Fetch my-batches and update paid data when user is updated
  const fetchUserBatchesAndPaidData = useCallback(
    async (userData: User) => {
      if (!userData?.id) return null;

      try {
        // Fetch my-batches
        const batchesResponse = await Client.get('batch-service/v4/batches/my-batches', {
          params: { mode: 1, filter: true },
        });
        if (batchesResponse?.data) {
          handlePurchasedBatchesDataUpdate(batchesResponse.data);
        } else {
          handlePurchasedBatchesDataUpdate(null);
        }
        return batchesResponse?.data;
      } catch (error) {
        // On error, set to null to clear any existing data
        handlePurchasedBatchesDataUpdate(null);
        return null;
      }
    },
    [handlePurchasedBatchesDataUpdate]
  );

  // Initialize user and paid data from localStorage on mount
  useEffect(() => {
    if (isInitialized || initializationInProgress.current) {
      console.log('UserContext: Initialization already completed or in progress, skipping...');
      return;
    }

    console.log('UserContext: Starting initialization...');
    const initializeUser = async () => {
      initializationInProgress.current = true;

      try {
        if (typeof window !== 'undefined') {
          hydrateSessionFromAppQuery();

          // Check if user has a valid token
          const token =
            CookieService.getValue(CookieKeys.TOKEN_CONTEXT)?.token ||
            localStorage.getItem('TOKEN') ||
            localStorage.getItem('token');

          if (token) {
            // Always call API on every session load if token exists
            console.log('UserContext: Token found, calling self API on session start...');
            await fetchUserData();
            setHasCalledAPI(true); // Mark as called for this session
          } else {
            // No token, check localStorage for cached user data
            console.log('UserContext: No token found, checking localStorage...');
            const userString = window.localStorage.getItem('user');
            if (userString) {
              const userData = JSON.parse(userString);
              setUser(userData);

              // Initialize Moengage for user if exists
              if (userData && userData.id) {
                loadMoengageScript();
              }
            } else {
              console.log('UserContext: No user data found in localStorage and no token');
            }
            setHasCalledAPI(true); // Mark as called to prevent retries
          }

          // Initialize paid data
          const initialPaidData = getPaidData();
          setPaidDataState(initialPaidData);

          // Initialize userSelectedCohortId from session storage
          const sessionCohortId = getSessionStorageValue('selectedCohortId');
          if (sessionCohortId) {
            setUserSelectedCohortId(sessionCohortId);
          }
        }
      } catch (error) {
        console.error('Error initializing user:', error);
        setUser(null);
      } finally {
        setIsLoading(false);
        setIsInitialized(true);
        initializationInProgress.current = false;
      }
    };

    initializeUser();
  }, [isInitialized]); // Remove fetchUserData from dependencies to prevent infinite loop

  // Update user in context and localStorage
  const updateUser = (userData: User | null) => {
    try {
      setUser(userData);

      if (typeof window !== 'undefined') {
        if (userData) {
          window.localStorage.setItem('user', JSON.stringify(userData));

          // Initialize Moengage for user if exists
          if (userData.id) {
            loadMoengageScript();
            triggerMoengageLoginIfUserIsPresent();

            // Fetch my-batches and update paid data for non-null user
            fetchUserBatchesAndPaidData(userData);
          }
        } else {
          window.localStorage.removeItem('user');
          // Clear purchased batches data and set paid data to 'non_loggedin'
          handlePurchasedBatchesDataUpdate(null);
        }
      }
    } catch (error) {
      console.error('Error updating user:', error);
    }
  };

  // Clear user from context and localStorage
  const clearUser = () => {
    setUser(null);
    // This will set paid data to 'non_loggedin' and clear purchased batches
    handlePurchasedBatchesDataUpdate(null);
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem('user');
    }
  };

  // Handle paid data update
  const updatePaidData = (newPaidData: PaidData) => {
    setPaidData(newPaidData);
    setPaidDataState(newPaidData);
  };

  // Clear paid data
  const clearPaidDataContext = () => {
    clearPaidData();
    setPaidDataState({});
  };

  const refetchUserBatches = useCallback(async () => {
    if (!user?.id) {
      return null;
    }
    return fetchUserBatchesAndPaidData(user);
  }, [user, fetchUserBatchesAndPaidData]);

  // Update journey home data
  const updateJourneyHomeData = useCallback((journeyData: JourneyHomeResponse | null) => {
    setJourneyHomeData(journeyData);
  }, []);

  // Trigger profile update to force refetch of home data
  const triggerProfileUpdate = useCallback(() => {
    setProfileUpdateTrigger(prev => prev + 1);
  }, []);

  const isUserLoggedIn = Boolean(user && user.id);

  const fetchClientConfig = async (category = 'BATCH_CONFIG'): Promise<any> => {
    try {
      const response = await Client.get(
        ApiServiceTypeEnum.CJR_API,
        `v1/client/metadata?category=${category}`
      );
      if (response?.data) {
        setClientConfig(prev => ({
          ...prev,
          [category]: response.data as MetadataBatchConfig | MetadataEbcConfig,
        }));
        return response.data;
      }
      setClientConfig(null);
      return {};
    } catch (error) {
      console.error('Error fetching client config:', error);
      return {};
    }
  };

  const contextValue: UserContextType = {
    user,
    isLoading,
    purchasedBatchesData,
    paidData,
    journeyHomeData,
    updateUser,
    clearUser,
    handlePurchasedBatchesDataUpdate,
    updatePaidData,
    clearPaidData: clearPaidDataContext,
    updateJourneyHomeData,
    isUserLoggedIn,
    refetchUserBatches,
    userSelectedCohortId,
    handleUserSelectedCohortIdUpdate,
    handleCohortExploration,
    handleActualCohortSwitch,
    fetchUserData,
    profileUpdateTrigger,
    triggerProfileUpdate,
    clientConfig,
    fetchClientConfig,
    globalRef,
    cohorts,
  };

  return <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>;
};

// Custom hook to use the user context
export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export default UserContext;
